import {makeStyles} from '@material-ui/core/styles';
import {Colors, greyColor3} from '../../../styles/Colors';
import * as Yup from 'yup';
import i18n from "../../../services/i18n";

export type PromptsFormType = {
    introPromptStatus: boolean;
    introPromptFileName: string;
    introPromptFile: File | null;
    introPromptConverting: boolean;
    menuPromptStatus: boolean;
    menuPromptFileName: string;
    menuPromptFile: File | null;
    menuPromptConverting: boolean;
    onTimeoutPromptStatus: boolean;
    onTimeoutPromptFileName: string;
    onTimeoutPromptFile: File | null;
    onTimeoutPromptConverting: boolean;
    onUnavailableSelectionPromptStatus: boolean;
    onUnavailableSelectionPromptFileName: string;
    onUnavailableSelectionPromptFile: File | null;
    onUnavailableSelectionPromptConverting: boolean;
};

const required = i18n.t('errors:common.missingFile');

const isFileRequired = (status: boolean, converting: boolean) =>
    status === true && converting !== true;

export const promptsFormValidationSchema = Yup.object().shape({
    introPromptStatus: Yup.boolean().required(),
    menuPromptStatus: Yup.boolean().required(),
    onTimeoutPromptStatus: Yup.boolean().required(),
    onUnavailableSelectionPromptStatus: Yup.boolean().required(),

    // the file is required only for a slot that is enabled and is not waiting
    // for the backend to convert an already uploaded prompt
    introPromptFileName: Yup.string().when(['introPromptStatus', 'introPromptConverting'], {
        is: isFileRequired,
        then: (schema) => schema.required(required).min(1, 'introPromptFileName cannot be empty'),
        otherwise: (schema) => schema.notRequired(),
    }),

    menuPromptFileName: Yup.string().when(['menuPromptStatus', 'menuPromptConverting'], {
        is: isFileRequired,
        then: (schema) => schema.required(required).min(1, 'introPromptFileName cannot be empty'),
        otherwise: (schema) => schema.notRequired(),
    }),

    onTimeoutPromptFileName: Yup.string().when(['onTimeoutPromptStatus', 'onTimeoutPromptConverting'], {
        is: isFileRequired,
        then: (schema) => schema.required(required).min(1, 'introPromptFileName cannot be empty'),
        otherwise: (schema) => schema.notRequired(),
    }),

    onUnavailableSelectionPromptFileName: Yup.string().when(
        ['onUnavailableSelectionPromptStatus', 'onUnavailableSelectionPromptConverting'],
        {
            is: isFileRequired,
            then: (schema) => schema.required(required).min(1, 'introPromptFileName cannot be empty'),
            otherwise: (schema) => schema.notRequired(),
        },
    ),
});

export const promptsFormDefaultValues: PromptsFormType = {
    introPromptStatus: false,
    introPromptFileName: '',
    introPromptFile: null,
    introPromptConverting: false,
    menuPromptStatus: false,
    menuPromptFileName: '',
    menuPromptFile: null,
    menuPromptConverting: false,
    onTimeoutPromptStatus: false,
    onTimeoutPromptFileName: '',
    onTimeoutPromptFile: null,
    onTimeoutPromptConverting: false,
    onUnavailableSelectionPromptStatus: false,
    onUnavailableSelectionPromptFileName: '',
    onUnavailableSelectionPromptFile: null,
    onUnavailableSelectionPromptConverting: false,
};

export type PromptFormProps = {
    handleSetIsValidFunc?: (funcName: string, func: () => boolean) => void;
    callback?: () => void;
    menuId?: number;
};

export const useStyles = makeStyles(() => ({
    buttonFile: {
        marginRight: '0px',
        width: 'auto',
        flex: 1
    },

    buttonFileTextField: {
        maxWidth: 375,
        width: 375,
        flex: 1
    },

    itemsContainer: {
        padding: '5px 25px 17px 25px',
        maxWidth: 700,
        display: 'flex',
        flexDirection: 'column',
        '& .MuiBox-root': {
            paddingLeft: 0,
            paddingRight:0
        },
    },
    columnItemsContainer: {
        flexDirection: 'column',
    },
    inputs: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'stretch',
    },
    rowBox: {
        padding: 0 + '!important',
        display: 'flex',
        alignItems: 'center',
    },
    rowBoxHeader: {
        marginRight: 20,
        fontSize: 16,
    },
    boldHeader: {
        fontWeight: 700,
    },
    musicRowBox: {
        padding: 0 + '!important',
        display: 'flex',
        alignItems: 'flex-end',
        marginTop: 11,

        '& .MuiButton-label': {
            color: Colors.White,
        },

        '& .MuiFormControl-root': {
            flex: 1,
            marginRight: 14,
        },
    },
    sectionSpace: {
        marginTop: 48,
    },
    subSectionSpace: {
        marginTop: 18,
    },
    numberInput: {
        width: 135,
        marginRight: 20,
    },
    inactiveActionSelect: {
        marginTop: 10,
        '& .MuiInputBase-input': {
            padding: '5px 10px !important',
        },
    },
    selectMaxWidth: {
        width: 475,
        maxWidth: 475,
    },
    centeredGrid: {
        display: 'flex',
        alignItems: 'center',
    },
    playBeforeGrid: {
        margin: '10px 0',
    },
    uploadMargin: {
        marginLeft: 24,
        marginRight: 10,
    },
    selectMargin: {
        marginTop: 20,
    },
    switchLabel: {
        fontWeight: 'bold',
        color: Colors.Gray7,
        marginRight: 14,
    },
    numberPickerGrid: {
        marginTop: 10,
        '& .MuiFormControl-root': {
            flex: 'unset',
        },
    },
    fileGrid: {
        marginBottom: 20,
    },

    file: {
        paddingLeft: 0,
        paddingRight:0,
        '& .MuiGrid-root': {
            minWidth: 426,
            marginBottom: 6,
        },
    },

    audioPlayerRootContainer: {
        display: 'none !important'
    },

    playPauseButtonNotStarted: {
        '& svg': {
            '& path': {
                fill: 'rgba(33, 123, 199, 1) !important',
            },
        },
    },
    
    processingRow: {
        paddingLeft: 0,
        paddingRight: 0,
        marginBottom: 20,
    },

    processingIcon: {
        color: greyColor3,
        height: 20,
        width: 20,
        marginRight: 8,
    },

    processingLabel: {
        color: Colors.Gray5,
        fontSize: 14,
        marginRight: 6,
    },

    playPauseButtonLoading: {
        color: Colors.Primary2,
        height: '28px !important',
        width: '28px !important',
        marginRight: 6,
        '& .MuiCircularProgress-svg': {
            color: Colors.Primary2
        },
    },
}));
